import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { SubcategoryItem } from '../../data/catalog.models';

@Component({
  selector: 'app-subcategories-carousel',
  imports: [RouterLink, IconComponent],
  templateUrl: './subcategories-carousel.component.html',
  styleUrl: './subcategories-carousel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubcategoriesCarouselComponent {
  subcategories = input.required<SubcategoryItem[]>();

  @ViewChild('track') private trackRef!: ElementRef<HTMLElement>;

  protected readonly chevronLeft: LucideIconData = ChevronLeft;
  protected readonly chevronRight: LucideIconData = ChevronRight;

  protected readonly canScrollLeft = signal(false);
  protected readonly canScrollRight = signal(false);

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const el = this.trackRef?.nativeElement;
      if (!el) return;

      this.updateScrollState(el);

      const onScroll = (): void => this.updateScrollState(el);
      el.addEventListener('scroll', onScroll, { passive: true });

      let ro: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => this.updateScrollState(el));
        ro.observe(el);
      }

      this.destroyRef.onDestroy(() => {
        el.removeEventListener('scroll', onScroll);
        ro?.disconnect();
      });
    });
  }

  protected scrollPrev(): void {
    this.scrollBy(-1);
  }

  protected scrollNext(): void {
    this.scrollBy(1);
  }

  private scrollBy(direction: 1 | -1): void {
    if (!this.isBrowser) return;
    const el = this.trackRef?.nativeElement;
    if (!el) return;

    if (direction === 1 && !this.canScrollRight()) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (direction === -1 && !this.canScrollLeft()) {
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      return;
    }

    const step = el.clientWidth * 0.75;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  private updateScrollState(el: HTMLElement): void {
    this.canScrollLeft.set(el.scrollLeft > 1);
    this.canScrollRight.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }
}
